const MS_PER_DAY = 86_400_000;

/**
 * Cap the enumeration at ~1 year (366 = leap-year days). Intentional bound, not
 * an off-by-one: guards against an unbounded range producing a huge list.
 */
export const MAX_ENUMERATED_DATES = 366;

/**
 * Enumerate inclusive `YYYY-MM-DD` dates from start to end (UTC, timezone-safe).
 *
 * Returns `[]` for an unparseable bound or an end before the start, so a caller
 * never has to guard against a malformed range.
 */
export const enumerateDates = (start: string, end: string): string[] => {
    const startMs = Date.parse(`${start}T00:00:00Z`);
    const endMs = Date.parse(`${end}T00:00:00Z`);
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
        return [];
    }
    const dates: string[] = [];
    for (
        let ms = startMs;
        ms <= endMs && dates.length < MAX_ENUMERATED_DATES;
        ms += MS_PER_DAY
    ) {
        dates.push(new Date(ms).toISOString().slice(0, 10));
    }
    return dates;
};

/**
 * The start of the longest window `enumerateDates` can actually produce for
 * this end date: `start` itself when the requested window already fits, a later
 * date when it has to be shortened to `maxDays`.
 *
 * The point is to let a caller shorten its window KNOWINGLY, and say so,
 * instead of handing `enumerateDates` a range it will quietly cut off at the
 * cap. Comparing the result against the `start` that was passed in tells the
 * caller whether anything was dropped. Unparseable bounds return `start`
 * unchanged: `enumerateDates` produces nothing for those either way.
 */
export const clampWindowStart = (
    start: string,
    end: string,
    maxDays: number = MAX_ENUMERATED_DATES
): string => {
    const startMs = Date.parse(`${start}T00:00:00Z`);
    const endMs = Date.parse(`${end}T00:00:00Z`);
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
        return start;
    }
    const earliestMs = endMs - (maxDays - 1) * MS_PER_DAY;
    return earliestMs > startMs
        ? new Date(earliestMs).toISOString().slice(0, 10)
        : start;
};
